import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  ArrowLeft,
  AlertCircle,
  RefreshCw,
  Coins,
  TrendingUp,
  Users,
  Award,
  Briefcase,
  Gift,
  Search,
  Plus,
  Minus,
  User,
  X
} from 'lucide-react';

const GET_TOKEN_STATS = gql`
  query GetTokenStats {
    tokenStats {
      totalInCirculation
      tokensFromJobs
      tokensFromSkills
      tokensFromPurchases
      tokensRedeemed
      usersWithTokens
      averageBalance
      topHolders {
        userId
        name
        email
        balance
      }
    }
  }
`;

const GET_TOKEN_ACTIVITY = gql`
  query GetTokenActivity($limit: Int) {
    tokenActivity(limit: $limit) {
      id
      type
      description
      userId
      userEmail
      amount
      direction
      createdAt
    }
  }
`;

const GET_ALL_USERS = gql`
  query GetAllUsers {
    users {
      id
      userId
      email
      name
      tokenBalance
    }
  }
`;

const GRANT_TOKENS = gql`
  mutation GrantTokens($userId: String!, $amount: Int!, $reason: String!) {
    grantTokens(userId: $userId, amount: $amount, reason: $reason) {
      userId
      name
      email
      balance
      granted
      reason
    }
  }
`;

const ADJUST_TOKENS = gql`
  mutation AdjustTokenBalance($userId: String!, $amount: Int!, $reason: String!) {
    adjustTokenBalance(userId: $userId, amount: $amount, reason: $reason) {
      userId
      previousBalance
      adjustment
      newBalance
      reason
    }
  }
`;

const AdminTokens = () => {
  const navigate = useNavigate();
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [grantData, setGrantData] = useState({ amount: 10, reason: '' });
  const [adjustData, setAdjustData] = useState({ amount: 0, reason: '' });
  const [successMessage, setSuccessMessage] = useState('');

  const { loading: statsLoading, error: statsError, data: statsData, refetch: refetchStats } = useQuery(GET_TOKEN_STATS);
  const { data: activityData, refetch: refetchActivity } = useQuery(GET_TOKEN_ACTIVITY, {
    variables: { limit: 50 }
  });
  const { data: usersData, error: usersError, loading: usersLoading } = useQuery(GET_ALL_USERS);
  
  // Debug - remove after fixing
  if (usersError) console.log('Users error:', usersError);

  const [grantTokens, { loading: granting }] = useMutation(GRANT_TOKENS, {
    onCompleted: (data) => {
      refetchStats();
      refetchActivity();
      setShowGrantModal(false);
      setSelectedUser(null);
      setGrantData({ amount: 10, reason: '' });
      setSuccessMessage(`Granted ${data.grantTokens.granted} tokens to ${data.grantTokens.name || data.grantTokens.email}`);
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  });

  const [adjustTokens, { loading: adjusting }] = useMutation(ADJUST_TOKENS, {
    onCompleted: (data) => {
      refetchStats();
      refetchActivity();
      setShowAdjustModal(false);
      setSelectedUser(null);
      setAdjustData({ amount: 0, reason: '' });
      setSuccessMessage(`Adjusted balance: ${data.adjustTokenBalance.previousBalance} → ${data.adjustTokenBalance.newBalance}`);
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  });

  const stats = statsData?.tokenStats || {};
  const activity = activityData?.tokenActivity || [];
  const allUsers = usersData?.users || [];

  // Filter users based on search
  const filteredUsers = allUsers.filter(user => 
    user.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
    user.name?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'job_completion': return <Briefcase size={16} className="text-blue-600" />;
      case 'skill_verification': return <Award size={16} className="text-purple-600" />;
      case 'purchase': return <Coins size={16} className="text-green-600" />;
      case 'grant': return <Gift size={16} className="text-yellow-600" />;
      default: return <Coins size={16} className="text-gray-600" />;
    }
  };

  const openGrantModal = (user = null) => {
    setSelectedUser(user);
    setUserSearch('');
    setGrantData({ amount: 10, reason: '' });
    setShowGrantModal(true);
  };

  const openAdjustModal = (user) => {
    setSelectedUser(user);
    setAdjustData({ amount: 0, reason: '' });
    setShowAdjustModal(true);
  };

  const submitGrant = () => {
    if (!selectedUser || grantData.amount <= 0 || !grantData.reason) return;
    grantTokens({
      variables: {
        userId: selectedUser.id || selectedUser.userId,
        amount: grantData.amount,
        reason: grantData.reason
      }
    });
  };

  const submitAdjust = () => {
    if (!selectedUser || adjustData.amount === 0 || !adjustData.reason) return;
    adjustTokens({
      variables: {
        userId: selectedUser.id || selectedUser.userId,
        amount: adjustData.amount,
        reason: adjustData.reason
      }
    });
  };

  // Quick grant presets
  const quickGrants = [
    { amount: 5, reason: 'Welcome bonus' },
    { amount: 10, reason: 'Referral reward' },
    { amount: 25, reason: 'Special contribution' },
    { amount: 50, reason: 'Community leader bonus' }
  ];

  if (statsLoading && !statsData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (statsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <p className="text-red-600">Error loading token stats: {statsError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={() => navigate('/admin/dashboard')}
              className="mr-3 p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Token Management</h1>
              <p className="text-sm text-gray-600">Monitor circulation and manage token distribution</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => openGrantModal()}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Gift size={18} />
              Grant Tokens
            </button>
            <button
              onClick={() => { refetchStats(); refetchActivity(); }}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mx-6 mt-4 p-4 bg-green-100 border border-green-300 text-green-800 rounded-lg flex items-center justify-between">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage('')}>
            <X size={18} />
          </button>
        </div>
      )}

      {/* Stats Overview */}
      <div className="p-6">
        <div className="grid grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Coins className="text-yellow-600" size={24} />
              </div>
              <div>
                <div className="text-3xl font-bold">{stats.totalInCirculation?.toLocaleString() || 0}</div>
                <div className="text-sm text-gray-600">Total in Circulation</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Briefcase className="text-blue-600" size={24} />
              </div>
              <div>
                <div className="text-3xl font-bold">{stats.tokensFromJobs?.toLocaleString() || 0}</div>
                <div className="text-sm text-gray-600">From Jobs</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Award className="text-purple-600" size={24} />
              </div>
              <div>
                <div className="text-3xl font-bold">{stats.tokensFromSkills?.toLocaleString() || 0}</div>
                <div className="text-sm text-gray-600">From Skills</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="text-green-600" size={24} />
              </div>
              <div>
                <div className="text-3xl font-bold">{stats.usersWithTokens || 0}</div>
                <div className="text-sm text-gray-600">Users with Tokens</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Top Token Holders */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">Top Token Holders</h2>
              <span className="text-sm text-gray-500">Avg: {stats.averageBalance || 0} tokens</span>
            </div>
            <div className="p-4">
              {stats.topHolders?.length > 0 ? (
                <div className="space-y-3">
                  {stats.topHolders.map((holder, index) => (
                    <div key={holder.userId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                          index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-400' : 'bg-gray-300'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium">{holder.name || 'Unknown'}</div>
                          <div className="text-sm text-gray-500">{holder.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="font-bold text-lg">{holder.balance}</div>
                          <div className="text-xs text-gray-500">tokens</div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => openGrantModal(holder)}
                            className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                            title="Grant tokens"
                          >
                            <Plus size={18} />
                          </button>
                          <button
                            onClick={() => openAdjustModal(holder)}
                            className="p-2 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors"
                            title="Adjust balance"
                          >
                            <Minus size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No token holders yet</p>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold">Recent Token Activity</h2>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              {activity.length > 0 ? (
                <div className="space-y-3">
                  {activity.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3 border-b last:border-0">
                      <div className="flex items-center gap-3">
                        {getActivityIcon(item.type)}
                        <div>
                          <div className="text-sm font-medium">{item.description}</div>
                          <div className="text-xs text-gray-500">
                            {item.userEmail || item.userId} • {formatDate(item.timestamp)}
                          </div>
                        </div>
                      </div>
                      <div className="text-green-600 font-bold">+{item.amount}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No recent activity</p>
              )}
            </div>
          </div>
        </div>

        {/* All Users - Quick Grant Section */}
        <div className="mt-6 bg-white rounded-lg shadow-sm">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold">All Users</h2>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search users..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg w-64"
              />
            </div>
          </div>
          <div className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(userSearch ? filteredUsers : allUsers.slice(0, 20)).map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <User size={16} className="text-blue-600" />
                          </div>
                          <span className="font-medium">{user.name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{user.email}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-bold">{user.tokenBalance || 0}</span>
                        <span className="text-gray-500 text-sm ml-1">tokens</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => openGrantModal({ ...user, userId: user.id })}
                            className="px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm font-medium flex items-center gap-1"
                          >
                            <Plus size={14} />
                            Grant
                          </button>
                          <button
                            onClick={() => openAdjustModal({ ...user, userId: user.id })}
                            className="px-3 py-1 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-sm font-medium flex items-center gap-1"
                          >
                            <Minus size={14} />
                            Adjust
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!userSearch && allUsers.length > 20 && (
                <p className="text-center text-gray-500 text-sm mt-4">
                  Showing 20 of {allUsers.length} users. Use search to find specific users.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Grant Modal */}
      {showGrantModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Grant Tokens</h3>
              <button onClick={() => setShowGrantModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            
            {!selectedUser ? (
              /* User Search */
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Search for User</label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    placeholder="Search by name or email..."
                    autoFocus
                  />
                </div>
                {userSearch && (
                  <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.slice(0, 10).map(user => (
                        <button
                          key={user.id}
                          onClick={() => setSelectedUser({ ...user, userId: user.id })}
                          className="w-full px-4 py-3 text-left hover:bg-blue-50 flex items-center justify-between border-b last:border-0"
                        >
                          <div>
                            <div className="font-medium">{user.name || 'Unknown'}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                          <div className="text-sm text-gray-500">{user.tokenBalance || 0} tokens</div>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-gray-500">No users found</div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* Selected User & Grant Form */
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="text-blue-600" size={20} />
                    </div>
                    <div>
                      <p className="font-medium">{selectedUser.name || 'Unknown'}</p>
                      <p className="text-sm text-gray-600">{selectedUser.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Current Balance</p>
                    <p className="font-bold text-lg">{selectedUser.tokens || selectedUser.balance || 0}</p>
                  </div>
                </div>
                
                {/* Quick Grant Presets */}
                <div>
                  <label className="block text-sm font-medium mb-2">Quick Grant</label>
                  <div className="grid grid-cols-4 gap-2">
                    {quickGrants.map((preset) => (
                      <button
                        key={preset.amount}
                        onClick={() => setGrantData({ amount: preset.amount, reason: preset.reason })}
                        className={`p-2 rounded-lg border text-center transition-colors ${
                          grantData.amount === preset.amount && grantData.reason === preset.reason
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-200 hover:border-green-300'
                        }`}
                      >
                        <div className="font-bold">{preset.amount}</div>
                        <div className="text-xs text-gray-500 truncate">{preset.reason}</div>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Custom Amount</label>
                  <input
                    type="number"
                    value={grantData.amount}
                    onChange={(e) => setGrantData({...grantData, amount: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Reason</label>
                  <textarea
                    value={grantData.reason}
                    onChange={(e) => setGrantData({...grantData, reason: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={2}
                    placeholder="Reason for granting tokens..."
                  />
                </div>
                
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Current Balance:</span>
                    <span>{selectedUser.tokens || selectedUser.balance || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Grant Amount:</span>
                    <span>+{grantData.amount}</span>
                  </div>
                  <div className="flex justify-between font-bold border-t mt-2 pt-2">
                    <span>New Balance:</span>
                    <span>{(selectedUser.tokens || selectedUser.balance || 0) + grantData.amount}</span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 mt-6">
              {selectedUser ? (
                <>
                  <button
                    onClick={submitGrant}
                    disabled={grantData.amount <= 0 || !grantData.reason || granting}
                    className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
                  >
                    {granting ? (
                      <RefreshCw size={18} className="animate-spin" />
                    ) : (
                      <Gift size={18} />
                    )}
                    Grant {grantData.amount} Tokens
                  </button>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Back
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowGrantModal(false)}
                  className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Adjust Modal */}
      {showAdjustModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Adjust Token Balance</h3>
              <button onClick={() => setShowAdjustModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <User className="text-orange-600" size={20} />
                  </div>
                  <div>
                    <p className="font-medium">{selectedUser.name || 'Unknown'}</p>
                    <p className="text-sm text-gray-600">{selectedUser.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Current Balance</p>
                  <p className="font-bold text-lg">{selectedUser.tokens || selectedUser.balance || 0}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Adjustment Amount</label>
                <input
                  type="number"
                  value={adjustData.amount}
                  onChange={(e) => setAdjustData({...adjustData, amount: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use negative number to reduce balance (e.g., -10)
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Reason (required)</label>
                <textarea
                  value={adjustData.reason}
                  onChange={(e) => setAdjustData({...adjustData, reason: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                  placeholder="Reason for adjustment..."
                />
              </div>
              
              <div className={`p-3 rounded-lg ${adjustData.amount >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex justify-between text-sm">
                  <span>Current Balance:</span>
                  <span>{selectedUser.tokens || selectedUser.balance || 0}</span>
                </div>
                <div className={`flex justify-between text-sm ${adjustData.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <span>Adjustment:</span>
                  <span>{adjustData.amount >= 0 ? '+' : ''}{adjustData.amount}</span>
                </div>
                <div className="flex justify-between font-bold border-t mt-2 pt-2">
                  <span>New Balance:</span>
                  <span>{Math.max(0, (selectedUser.tokens || selectedUser.balance || 0) + adjustData.amount)}</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={submitAdjust}
                disabled={adjustData.amount === 0 || !adjustData.reason || adjusting}
                className="flex-1 bg-orange-600 text-white py-2 rounded-lg hover:bg-orange-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
              >
                {adjusting ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : (
                  <Coins size={18} />
                )}
                Adjust Balance
              </button>
              <button
                onClick={() => setShowAdjustModal(false)}
                className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTokens;